// Financial Logs — the one place for cost/price audit data. Financial role only
// (the route guard + nav use the strict can_financial check; the API endpoints
// are require-financial, NOT admin-bypassed).
//
// Two tabs:
//   - Transactions: the inventory_transactions trail (receive/consume/transfer/
//     adjust) with per-transaction cost. GET /api/inventory/transactions.
//   - Price Changes: manual unit_cost overrides across all SKUs (the same data
//     shown per-SKU in the Adjust modal's "Recent overrides"). GET
//     /api/inventory/price-history (no part_catalog_id → all).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { DataTable } from '../components/table/DataTable';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import PageLayout from '../components/ui/PageLayout';
import SearchField from '../components/ui/SearchField';
import { Select } from '../components/ui/Select';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import FadeIn from '../components/ui/FadeIn';
import { LoadMore } from '../components/ui/Pager';
import { formatTimestamp } from '../utils/date-utils';
import type { LocationRow } from '../Receiving/types';

type TxType = 'receive' | 'consume' | 'transfer' | 'adjust';

interface TxRow {
  id: number;
  part_catalog_id: number;
  receipt_item_id: string | null;
  transaction_type: TxType;
  quantity_delta: number;
  unit_cost_cents: number | null;
  occurred_at: string;
  note: string | null;
  from_location_id: number | null;
  to_location_id: number | null;
  sku: string | null;
  description: string | null;
  from_location_name: string | null;
  to_location_name: string | null;
  performed_by_name: string | null;
}

interface PriceChangeRow {
  id: number;
  part_catalog_id: number;
  old_unit_cost: string | null;
  new_unit_cost: string | null;
  reason: string | null;
  changed_at: string;
  sku: string | null;
  description: string | null;
  changed_by_name: string | null;
}

const PAGE_SIZE = 100;

const TYPE_BADGE: Record<TxType, string> = {
  receive: 'bg-green-500/15 text-green-300',
  consume: 'bg-red-500/15 text-red-300',
  transfer: 'bg-blue-500/15 text-blue-300',
  adjust: 'bg-amber-500/15 text-amber-300',
};

const fmtCents = (c: number | null) =>
  c === null || c === undefined ? '—' : `$${(c / 100).toFixed(2)}`;

const fmtDollars = (n: string | null) =>
  n === null || n === undefined || n === '' ? '—' : `$${parseFloat(n).toFixed(2)}`;

const locationFlow = (r: TxRow) => {
  const from = r.from_location_name;
  const to = r.to_location_name;
  if (from && to) return `${from} → ${to}`;
  if (to) return `→ ${to}`;
  if (from) return `${from} →`;
  return '—';
};

// ---------------------------------------------------------------------------
// The two logs, as shared-DataTable columns.
//
// Module scope per the DataTable contract: every cell is pure over its row and
// nothing closes over page state, so neither table needs `meta`. No `sortable`
// and no accessorFn on either — both are LEDGERS, read newest-first in the
// order the server sends them, and the pile grows by APPENDED pages, so a
// client-side re-sort would order the rows that happen to be loaded rather
// than the trail itself.
//
// Alignment and tabular figures ride `meta`, never a class string, and the
// vessel owns the td: a color that varies per row rides a span inside the cell
// (the Qty Δ sign) rather than the cell's own class list.
// ---------------------------------------------------------------------------

const txRowId = (r: TxRow) => String(r.id);
const priceRowId = (r: PriceChangeRow) => String(r.id);

const TX_COLUMNS: ColumnDef<TxRow, any>[] = [
  {
    id: 'when',
    header: 'When',
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => formatTimestamp(row.original.occurred_at, 'MMM D, YYYY · h:mm A'),
  },
  {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => (
      <span
        className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${TYPE_BADGE[row.original.transaction_type] || 'bg-surface-container-highest text-on-surface-variant'}`}
      >
        {row.original.transaction_type}
      </span>
    ),
  },
  {
    id: 'sku',
    header: 'SKU',
    meta: { cellClass: 'font-mono text-primary whitespace-nowrap' },
    cell: ({ row }) =>
      row.original.sku ? (
        <Link to={`/parts-catalog/${row.original.part_catalog_id}`} className="hover:underline">
          {row.original.sku}
        </Link>
      ) : (
        '—'
      ),
  },
  {
    id: 'delta',
    header: 'Qty Δ',
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => (
      <span className={row.original.quantity_delta < 0 ? 'text-red-300' : 'text-green-300'}>
        {row.original.quantity_delta > 0
          ? `+${row.original.quantity_delta}`
          : row.original.quantity_delta}
      </span>
    ),
  },
  {
    id: 'location',
    header: 'Location',
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => locationFlow(row.original),
  },
  {
    id: 'unit_cost',
    header: 'Unit cost',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => fmtCents(row.original.unit_cost_cents),
  },
  {
    id: 'by',
    header: 'By',
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => row.original.performed_by_name || '—',
  },
  {
    // The td is the element that truncates, so the tooltip belongs on it — and
    // jsdom folds `title` into an accessible name, which is why it stays there
    // rather than moving onto an inner span.
    id: 'note',
    header: 'Note',
    meta: {
      cellClass: 'text-on-surface-variant max-w-xs truncate',
      cellAttrs: (r: TxRow) => ({ title: r.note || '' }),
    },
    cell: ({ row }) => row.original.note || '—',
  },
];

const PRICE_COLUMNS: ColumnDef<PriceChangeRow, any>[] = [
  {
    id: 'when',
    header: 'When',
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => formatTimestamp(row.original.changed_at, 'MMM D, YYYY · h:mm A'),
  },
  {
    id: 'sku',
    header: 'SKU',
    meta: { cellClass: 'font-mono text-primary whitespace-nowrap' },
    cell: ({ row }) =>
      row.original.sku ? (
        <Link to={`/parts-catalog/${row.original.part_catalog_id}`} className="hover:underline">
          {row.original.sku}
        </Link>
      ) : (
        '—'
      ),
  },
  {
    // Old dimmer than New by one step, deliberately: the figure that is true
    // now is the one being read, and the one it replaced is context.
    id: 'old',
    header: 'Old',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => fmtDollars(row.original.old_unit_cost),
  },
  {
    id: 'new',
    header: 'New',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    cell: ({ row }) => fmtDollars(row.original.new_unit_cost),
  },
  {
    id: 'by',
    header: 'By',
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => row.original.changed_by_name || '—',
  },
  {
    id: 'reason',
    header: 'Reason',
    meta: {
      cellClass: 'text-on-surface-variant max-w-xs truncate',
      cellAttrs: (r: PriceChangeRow) => ({ title: r.reason || '' }),
    },
    cell: ({ row }) => row.original.reason || '—',
  },
];

// ── Transactions tab ──────────────────────────────────────────────────────
const TransactionsTab: React.FC = () => {
  const api = useApi();
  const toast = useToast();

  const [rows, setRows] = useState<TxRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | TxType>('');
  const [locationFilter, setLocationFilter] = useState('');

  const fetchPage = useCallback(
    async (offset: number) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (typeFilter) params.set('transaction_type', typeFilter);
      if (locationFilter) params.set('location_id', locationFilter);
      return (await api.get(`/inventory/transactions?${params.toString()}`)) as TxRow[];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typeFilter, locationFilter]
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await fetchPage(0)) || [];
      setRows(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = (await fetchPage(rows.length)) || [];
      setRows((prev) => [...prev, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLocations((await api.get('/locations')) as LocationRow[]);
      } catch {
        // Not fatal — the location filter just won't populate.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (r.sku || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q) ||
        (r.note || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div className="space-y-5">
      <p className="text-sm text-on-surface-variant max-w-3xl">
        Every movement of stock — receives, consumes, transfers, and manual adjustments —
        newest first. Costs are shown per transaction.
      </p>

      <div className="flex gap-2 flex-wrap items-center">
        <SearchField
          label="Filter loaded rows by SKU, description, note"
          value={search}
          onChange={setSearch}
          placeholder="Filter loaded rows by SKU, description, note…"
          className="min-w-[200px] max-w-md flex-1"
        />
        <Select
          label="Transaction type"
          width="fit"
          value={typeFilter}
          onChange={(key) => setTypeFilter(key as '' | TxType)}
          options={[
            { key: '', label: 'All types' },
            { key: 'receive', label: 'Receive' },
            { key: 'consume', label: 'Consume' },
            { key: 'transfer', label: 'Transfer' },
            { key: 'adjust', label: 'Adjust' },
          ]}
        />
        <Select
          label="Location"
          width="fit"
          value={locationFilter}
          onChange={setLocationFilter}
          options={[
            { key: '', label: 'All locations' },
            ...locations.map((l) => ({ key: String(l.id), label: l.name })),
          ]}
        />
      </div>

      {/* The vessel is the DataTable's — the page's own box, header band and row
          borders are gone. The count rides the strip where it names the pile it
          counts, and Load more rides the footer band: a control that EXTENDS
          the pile belongs under it, not floating below a box.

          Sticky for the same reason the receiving hub's tabbed tables are: this
          is the only table on screen while its tab is up, the tabs unmount
          rather than hide (so the measured cap is never taken off a
          display:none element), and a ledger that grows by 100 rows a click
          should scroll inside its vessel instead of pushing the filters and the
          Load more button off the page. */}
      <DataTable<TxRow>
        data={filtered}
        columns={TX_COLUMNS}
        getRowId={txRowId}
        stickyHeader
        strip={
          <div className="ml-auto text-xs text-on-surface-variant tabular-nums whitespace-nowrap">
            {filtered.length} shown
          </div>
        }
        footer={
          // Append pagination stays PAGE-OWNED: the page fetches the next page
          // and appends to the array it hands over as `data`. This is a prop
          // node rather than a cell renderer, so reading page state in a
          // closure here is exactly right — `loadingMore` is the button's own
          // disabled + busy label, so a second click cannot fire mid-fetch.
          !loading && hasMore ? (
            <LoadMore more busy={loadingMore} onMore={loadMore} />
          ) : undefined
        }
        loading={loading}
        loadingContent={
          <SkeletonGroup label="Loading" className="space-y-2 py-6">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </SkeletonGroup>
        }
        empty={
          <EmptyState variant="area">No transactions match the current filter.</EmptyState>
        }
      />
    </div>
  );
};

// ── Price Changes tab ─────────────────────────────────────────────────────
const PriceChangesTab: React.FC = () => {
  const api = useApi();
  const toast = useToast();

  const [rows, setRows] = useState<PriceChangeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState('');

  const fetchPage = useCallback(async (offset: number) => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    return (await api.get(`/inventory/price-history?${params.toString()}`)) as PriceChangeRow[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await fetchPage(0)) || [];
      setRows(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load price changes');
    } finally {
      setLoading(false);
    }
  }, [fetchPage, toast]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = (await fetchPage(rows.length)) || [];
      setRows((prev) => [...prev, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    reload();
  }, [reload]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (r.sku || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q) ||
        (r.reason || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div className="space-y-5">
      <p className="text-sm text-on-surface-variant max-w-3xl">
        Manual unit-cost overrides made from the Adjust modal — newest first. These set a SKU's
        cost directly (used mainly for legacy parts that were never formally received); a later
        receive or consume of the same SKU recomputes the weighted average and supersedes the
        override.
      </p>

      <div className="flex gap-2 flex-wrap items-center">
        <SearchField
          label="Filter loaded rows by SKU, description, reason"
          value={search}
          onChange={setSearch}
          placeholder="Filter loaded rows by SKU, description, reason…"
          className="min-w-[200px] max-w-md flex-1"
        />
      </div>

      {/* Same vessel, same bands, same reasoning as the Transactions tab. */}
      <DataTable<PriceChangeRow>
        data={filtered}
        columns={PRICE_COLUMNS}
        getRowId={priceRowId}
        stickyHeader
        strip={
          <div className="ml-auto text-xs text-on-surface-variant tabular-nums whitespace-nowrap">
            {filtered.length} shown
          </div>
        }
        footer={
          !loading && hasMore ? (
            <LoadMore more busy={loadingMore} onMore={loadMore} />
          ) : undefined
        }
        loading={loading}
        loadingContent={
          <SkeletonGroup label="Loading" className="space-y-2 py-6">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </SkeletonGroup>
        }
        empty={
          <EmptyState variant="area">No price overrides recorded yet.</EmptyState>
        }
      />
    </div>
  );
};

// ── Page shell ────────────────────────────────────────────────────────────
const TABS = [
  { key: 'transactions', label: 'Transactions' },
  { key: 'prices', label: 'Price Changes' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const FinancialLogsPage: React.FC = () => {
  const canSeeFinancials = useUserStore((s) => !!s.user.can_financial);
  const [tab, setTab] = useState<TabKey>('transactions');

  if (!canSeeFinancials) {
    return (
      <PageLayout kind="workspace" className="pt-4">
        <div className="max-w-3xl">
          <h1 className="text-2xl font-bold text-primary mb-2">Financial Logs</h1>
          <p className="text-sm text-on-surface-variant">
            Financial logs are restricted to the Financial role.
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      kind="workspace"
      className="space-y-5 pt-4"
      header={<h1 className="text-2xl font-bold text-primary">Financial Logs</h1>}
    >
      <div className="flex gap-1 border-b border-gray-700/60">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <FadeIn key={tab}>
        {tab === 'transactions' ? <TransactionsTab /> : <PriceChangesTab />}
      </FadeIn>
    </PageLayout>
  );
};

export default FinancialLogsPage;
